import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSubdomain } from '../contexts/SiteContext';
import { useAuth } from '../contexts/AuthContext';
import { useEffect, useState } from 'react';

/**
 * Hook para manejar la navegación basada en subdomains
 * Proporciona funcionalidad para navegar entre subdomains y mantener el contexto
 */
export const useSiteNavigation = () => {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    sites, 
    activeSite, 
    changeActiveSite, 
    loadSites,
    executeSubdomainRedirect,
    validateSubdomainAccess
  } = useSubdomain();
  const { hasRole } = useAuth();
  const [isLoading] = useState(false);

  // Verificar si el usuario es manager
  const isManager = hasRole('manager');

  // Cargar sites si es manager y no hay sites cargados
  useEffect(() => {
    if (isManager && sites.length === 0) {
      loadSites(false);
    }
  }, [isManager, sites.length, loadSites]);

  // Cambiar site activo cuando cambia el siteId en la URL
  useEffect(() => {
    if (siteId && sites.length > 0) {
      const site = sites.find(s => s.id === siteId);
      if (site && (!activeSite || activeSite.id !== siteId)) {
        changeActiveSite(site);
        
        // Verificar si necesita redirección de subdomain
        executeSubdomainRedirect(site);
      }
    }
  }, [siteId, sites, activeSite, changeActiveSite, executeSubdomainRedirect]);

  // Navegar a un site específico
  const navigateToSite = (siteId, path = '') => {
    if (!isManager) {
      console.warn('Solo los managers pueden navegar entre sites');
      return;
    }

    const site = sites.find(s => s.id === siteId);
    if (!site) {
      console.error('Site no encontrado:', siteId);
      return;
    }

    const basePath = `/site/${siteId}`;
    const fullPath = path ? `${basePath}${path.startsWith('/') ? path : `/${path}`}` : basePath;
    
    navigate(fullPath);
  };

  // Navegar a la página principal de un site
  const navigateToSiteHome = (siteId) => {
    navigateToSite(siteId, '/dashboard');
  };

  // Navegar a la gestión de miembros de un site
  const navigateToSiteMembers = (siteId) => {
    navigateToSite(siteId, '/members');
  };

  // Navegar a la gestión de locations de un site
  const navigateToSiteLocations = (siteId) => {
    navigateToSite(siteId, '/locations');
  };

  // Navegar a la gestión de horarios de un site
  const navigateToSiteSchedules = (siteId) => {
    navigateToSite(siteId, '/schedules');
  };

  // Navegar a la configuración de un site
  const navigateToSiteSettings = (siteId) => {
    navigateToSite(siteId, '/settings');
  };

  // Obtener la URL base del site actual
  const getCurrentSiteBaseUrl = () => {
    if (!activeSite) return null;
    return `/site/${activeSite.id}`;
  };

  // Verificar si estamos en un contexto de site
  const isInSiteContext = () => {
    return !!siteId && !!activeSite;
  };

  // Obtener el site actual basado en la URL
  const getCurrentSite = () => {
    if (siteId && sites.length > 0) {
      return sites.find(s => s.id === siteId);
    }
    return activeSite;
  };

  // Verificar si el usuario tiene acceso al site actual
  const hasAccessToCurrentSite = () => {
    console.log('🚀 === VERIFICACIÓN DE ACCESO AL SITE INICIADA ===');
    console.log('👤 User Info:', {
      isManager,
      siteId,
      currentPath: location.pathname
    });
    
    if (!isManager) {
      console.log('❌ ACCESO DENEGADO: Usuario no es manager');
      return false;
    }
    
    if (!siteId) {
      console.log('🏠 PÁGINA PRINCIPAL: Acceso permitido (la redirección se maneja en SiteContext)');
      console.log('🚀 === VERIFICACIÓN DE ACCESO AL SITE COMPLETADA ===');
      return true; // Si no hay siteId, es la página principal
    }
    
    const site = sites.find(s => s.id === siteId);
    if (!site) {
      console.log('❌ ACCESO DENEGADO: Site no encontrado en la lista');
      console.log('📋 Sites disponibles:', sites.map(s => ({ id: s.id, name: s.name, subdomain: s.subdomain })));
      return false;
    }
    
    console.log('🔍 Site encontrado:', {
      siteId: site.id,
      siteName: site.name,
      siteSubdomain: site.subdomain
    });
    
    // Verificar acceso basado en subdomain
    console.log('🔍 Llamando a validateSubdomainAccess...');
    const accessValidation = validateSubdomainAccess(site);
    console.log('🎯 Resultado de validación:', accessValidation);
    console.log('🚀 === VERIFICACIÓN DE ACCESO AL SITE COMPLETADA ===');
    
    return accessValidation.hasAccess;
  };

  // Redirigir al primer site disponible si no hay siteId
  const redirectToFirstSite = () => {
    if (isManager && sites.length > 0 && !siteId) {
      navigateToSiteHome(sites[0].id);
    }
  };

  // Obtener breadcrumbs para la navegación
  const getBreadcrumbs = () => {
    const breadcrumbs = [];
    
    if (isManager) {
      breadcrumbs.push({ label: 'Mis Sites', path: '/manager' });
    }
    
    if (activeSite) {
      breadcrumbs.push({ 
        label: activeSite.name, 
        path: `/site/${activeSite.id}/dashboard` 
      });
    }
    
    // Agregar breadcrumb específico de la página actual
    const currentPath = location.pathname;
    if (currentPath.includes('/members')) {
      breadcrumbs.push({ label: 'Miembros', path: currentPath });
    } else if (currentPath.includes('/locations')) {
      breadcrumbs.push({ label: 'Ubicaciones', path: currentPath });
    } else if (currentPath.includes('/schedules')) {
      breadcrumbs.push({ label: 'Horarios', path: currentPath });
    } else if (currentPath.includes('/settings')) {
      breadcrumbs.push({ label: 'Configuración', path: currentPath });
    }
    
    return breadcrumbs;
  };

  return {
    // Estado
    currentSiteId: siteId,
    currentSite: getCurrentSite(),
    isLoading,
    isInSiteContext: isInSiteContext(),
    hasAccess: hasAccessToCurrentSite(),
    breadcrumbs: getBreadcrumbs(),
    
    // Navegación
    navigateToSite,
    navigateToSiteHome,
    navigateToSiteMembers,
    navigateToSiteLocations,
    navigateToSiteSchedules,
    navigateToSiteSettings,
    getCurrentSiteBaseUrl,
    redirectToFirstSite,
    
    // Utilidades
    isManager
  };
};

export default useSiteNavigation;
